import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  Renderer2,
  TransferState,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AppState } from './root-store/state';
import {
  loadCommonLookups,
  PartnerSitesLookupActions,
  getNewAccessToken,
  loadLanguage,
  loadFavorites,
  loadProviderWebsiteInfo,
  ConfigsActions,
  facebookSdkInit,
  setCurrentDate,
  getGuid,
} from './root-store/actions';
import { DOCUMENT, registerLocaleData } from '@angular/common';
import localeGeo from '@angular/common/locales/ka';
import localeArm from '@angular/common/locales/hy';
import localeRus from '@angular/common/locales/ru';
import localeKaz from '@angular/common/locales/kk';
import { combineLatest, Observable } from 'rxjs';
import { selectFacebookPageId } from './root-store/selectors';
import { NavigationEnd, Router } from '@angular/router';
import { filter, skip, take } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { FacebookService } from '@hrra/integrations';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { DeviceDetectionService, EnvService, LocalDataStorageService, LogginService, SsrService } from '@hrra/core';
import { SeoLinksService } from './core/seo-links.service';
import { LocaleService } from './core/locale.service';
import { usePreset } from '@primeng/themes';
import { cv } from './primeng-themes/cv';
import { primengThemes } from './primeng-themes/primeng-themes';
import opentelemetry from '@opentelemetry/api';
import pino from 'pino';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


registerLocaleData(localeGeo, 'ka');
registerLocaleData(localeArm, 'hy');
registerLocaleData(localeRus, 'ru');
registerLocaleData(localeKaz, 'kk');

@Component({
    selector: 'app-root',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  title = 'public-web-app';

  date: Date;

  private _logger: pino.Logger;

  private destroyRef = inject(DestroyRef);

  get logger() {
    return this._logger;
  }

  facebookPageId$: Observable<string>;

  constructor(
    private gtmService: GoogleTagManagerService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private _document,
    private router: Router,
    private ssr: SsrService,
    private loggingService: LogginService,
    private envService: EnvService,
    private deviceDetectionService: DeviceDetectionService,
    private seoLinksService: SeoLinksService,
    private localeService: LocaleService
  ) {
    this.date = new Date();
    this._logger = this.loggingService.createLogger('app-component');
  }

  ngOnInit(): void {

    this.deviceDetectionService.init(true);

    usePreset(primengThemes[this.envService.theme]);

    this.store.dispatch(setCurrentDate({ payload: { date: this.date } }));
    this.store.dispatch(getGuid());

    this.store.dispatch(loadLanguage());
    this.seoLinksService.init();
    // Reflect the active locale on <html lang> (served HTML is otherwise always "en").
    this.renderer2.setAttribute(this._document.documentElement, 'lang', this.localeService.getLocale());
    this.store.dispatch(loadProviderWebsiteInfo());
    this.store.dispatch(loadCommonLookups());
    this.store.dispatch({
      type: PartnerSitesLookupActions.LoadPartnerSitesLookup,
    });
    //this.store.dispatch({type: CurrentInfoActions.LoadCurrentInfo});
    this.store.dispatch(getNewAccessToken());
    this.store.dispatch({ type: ConfigsActions.LoadConfigs });

    if (this.ssr.isPlatformBrowser()) {
      this.store.dispatch(loadFavorites());
    }

    if(this.ssr.isPlatformServer()){
      this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
        if (event instanceof NavigationEnd) {
          let currentRoute = this.router.routerState.root;
          const routeSegements = [];
          do{
            routeSegements.push(currentRoute.routeConfig?.path);
            currentRoute = currentRoute.firstChild;
          }
          while(currentRoute);

          const routePath = routeSegements.join('/');

          this.logger.debug(`Http route: ${routePath}`);
          
          opentelemetry.trace.getActiveSpan()?.setAttribute('http.route', routePath);
        }
      });
    }

    this.facebookPageId$ = this.store.pipe(select(selectFacebookPageId));

    // if (this.ssr.isPlatformBrowser()) {
    //   this.store.dispatch(facebookSdkInit());
    // }
  }

  ngOnDestroy() {}
}
